import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { StaffMatchCard } from "@/components/StaffMatchCard";
import { BellRing, Loader2, Save, FlaskConical, PlayCircle, Lock } from "lucide-react";

const RULES = [
  ["enquiry_overdue_hours", "Remind about unanswered enquiries after (hours)"],
  ["reply_overdue_hours", "Remind about unanswered customer replies after (hours)"],
  ["action_reminder_hours", "Remind about other open actions after (hours)"],
  ["reminder_repeat_hours", "Repeat reminders every (hours)"],
  ["max_reminders_per_item", "Maximum reminders per item"],
  ["quote_followup_days", "Quote follow-up after (days)"],
  ["engineer_late_minutes", "Engineer not arrived after start + (minutes)"],
  ["ticket_overdue_hours", "Ticket overdue after (hours)"],
  ["escalate_after_hours", "Escalate high-priority items after (hours)"],
  ["max_per_event_per_hour", "Flood limit: per notification type per hour"],
  ["max_per_company_per_hour", "Flood limit: whole company per hour"],
  ["max_critical_per_hour", "Critical alert ceiling per hour (security alerts exempt)"],
];

const Card = ({ title, children, testid }) => (
  <section className="bg-white rounded-[18px] border border-black/[0.04] shadow-card p-5 min-w-0 overflow-hidden" data-testid={testid}>
    <h2 className="text-[15px] font-semibold text-[#1D1D1F] mb-3">{title}</h2>{children}
  </section>
);

const GROUPS = [
  ["enquiries", "New enquiries (pilot group)", "Unassigned new enquiries alert only these people. Everyone with Enquiries access still sees the jobs."],
  ["sales_inbox", "Sales Inbox (customer SMS replies)", "Replies on jobs without a valid Sales Contact, and texts from unknown numbers, alert only these people."],
  ["operations", "Operations Alerts", "Scheduling, assignment, unavailability and allocation alerts go only to these people (Operations managers)."],
];

const RecipientsCard = ({ value, onChange }) => {
  const [eligible, setEligible] = useState(null);
  useEffect(() => { api.get("/notification-settings/eligible-recipients").then((r) => setEligible(r.data)).catch(() => setEligible({})); }, []);
  const toggle = (g, id, on) => onChange({ ...value, [g]: on ? [...new Set([...(value[g] || []), id])] : (value[g] || []).filter((x) => x !== id) });
  return (
    <Card title="Alert recipients for shared queues" testid="na-recipients">
      <div className="grid md:grid-cols-3 gap-5">
        {GROUPS.map(([g, title, help]) => (
          <div key={g} className="min-w-0" data-testid={`na-recipients-${g}`}>
            <div className="text-sm font-medium">{title}</div>
            <p className="text-[12px] text-slate-500 mb-2">{help}</p>
            <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
              {!eligible && <div className="py-2 text-sm text-slate-400">Loading…</div>}
              {(eligible?.[g] || []).map((u) => (
                <label key={u.id} className="flex items-center justify-between gap-3 py-1.5 text-sm cursor-pointer">
                  <span className="truncate">{u.name}<span className="text-slate-400"> · {u.email}</span></span>
                  <Switch checked={(value[g] || []).includes(u.id)} onCheckedChange={(v) => toggle(g, u.id, v)} data-testid={`na-recipient-${g}-${u.id}`} />
                </label>
              ))}
            </div>
            <div className="text-[12px] text-slate-500 mt-1" data-testid={`na-recipients-${g}-count`}>{(value[g] || []).length} selected</div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default function NotificationsAdmin() {
  const { roles } = useAuth();
  const [s, setS] = useState(null);
  const [ov, setOv] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/notification-settings").then((r) => setS(r.data)).catch(() => toast.error("Could not load settings"));
    api.get("/notification-admin/overview").then((r) => setOv(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRule = (k, v) => setS((x) => ({ ...x, rules: { ...x.rules, [k]: v } }));
  const setQ = (k, v) => setS((x) => ({ ...x, quiet_hours: { ...x.quiet_hours, [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/notification-settings", { rules: s.rules, quiet_hours: s.quiet_hours, rollout: s.rollout, recipients: s.recipients }); toast.success("Notification policy saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };
  const dryRun = async () => {
    setRunning(true);
    try { setPlan((await api.post("/notification-settings/dry-run")).data); } catch { toast.error("Dry run failed"); } finally { setRunning(false); }
  };
  const resume = (k) => api.post(`/notification-settings/resume/${k}`).then(() => { toast.success("Resumed"); load(); });
  const toggleType = (t, on) => setS((x) => ({ ...x, rollout: { ...x.rollout, enabled_types: on ? [...new Set([...x.rollout.enabled_types, t])] : x.rollout.enabled_types.filter((y) => y !== t) } }));
  const [briefing, setBriefing] = useState(null);
  useEffect(() => { api.get("/ops-briefing/usage").then((r) => setBriefing(r.data.settings)).catch(() => {}); }, []);
  const setBriefingCfg = (patch) => api.put("/ops-briefing/settings", patch).then((r) => { setBriefing(r.data); toast.success("Operations Briefing updated"); }).catch(() => toast.error("Could not update"));
  const label = (k) => (ov?.categories || []).find((c) => c.key === k)?.label || k;

  if (!s) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="p-6 lg:p-8 max-w-6xl space-y-5" data-testid="notifications-admin-page">
      <div className="flex flex-wrap items-center gap-3">
        <BellRing className="w-7 h-7 text-[#6E6E73]" />
        <div className="flex-1"><h1 className="ted-page-title">Notifications &amp; Alerts</h1>
          <p className="ted-subtle mt-1">Company notification policy. Changes are audited and only affect this company.</p></div>
        <Button variant="outline" onClick={dryRun} disabled={running} className="gap-1.5" data-testid="na-dry-run">{running ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Dry run</Button>
        <Button onClick={save} disabled={saving} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="na-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Button>
      </div>

      {plan && (
        <Card title={`Dry run: the next scheduled run would create ${plan.total} notification(s)`} testid="na-dry-run-result">
          <div className="flex flex-wrap gap-2 mb-3">{Object.entries(plan.by_type).map(([k, v]) => <span key={k} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium">{k}: {v}</span>)}</div>
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 text-sm">
            {plan.items.map((p, i) => <div key={i} className="py-1.5 flex gap-3"><span className="w-40 shrink-0 text-slate-500 truncate">{p.recipient}</span><span className="flex-1 truncate">{p.title}</span>{p.disabled_by_rollout && <span className="text-xs text-amber-700 shrink-0">type off</span>}<span className="text-xs text-slate-400 truncate max-w-[40%]">{p.reason}</span></div>)}
            {plan.total === 0 && <div className="py-4 text-slate-400">Nothing would be sent.</div>}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Staged rollout: new notification types" testid="na-rollout">
          <div className="flex items-center justify-between py-1 text-sm"><span className="font-medium">Enable all new types</span>
            <Switch checked={!!s.rollout?.all} onCheckedChange={(v) => setS((x) => ({ ...x, rollout: { ...x.rollout, all: v } }))} data-testid="na-rollout-all" /></div>
          <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-slate-100">
            {(s.staged_types || []).map((t) => (
              <div key={t} className="flex items-center justify-between py-1.5 text-sm"><span className="truncate pr-3">{label(t)}</span>
                <Switch checked={!!s.rollout?.all || s.rollout?.enabled_types?.includes(t)} disabled={!!s.rollout?.all} onCheckedChange={(v) => toggleType(t, v)} data-testid={`na-rollout-${t}`} /></div>
            ))}
          </div>
          <p className="text-[12px] text-slate-500 mt-2">Types added by the new notification system stay off until switched on here (then Save). Existing alerts such as holidays, approvals and security warnings are always on.</p>
        </Card>
        <Card title="Operations Briefing" testid="na-briefing">
          {!briefing ? <div className="text-sm text-slate-400">Loading…</div> : (<>
            <div className="flex items-center justify-between py-1.5 text-sm"><span>Show the rule-based briefing to managers</span><Switch checked={!!briefing.briefing_enabled} onCheckedChange={(v) => setBriefingCfg({ briefing_enabled: v })} data-testid="na-briefing-enabled" /></div>
            <div className="flex items-center justify-between py-1.5 text-sm"><span>AI recommendations on request</span><Switch checked={!!briefing.ai_enabled} onCheckedChange={(v) => setBriefingCfg({ ai_enabled: v })} data-testid="na-briefing-ai" /></div>
            <label className="mt-2 flex items-center justify-between gap-3 text-sm">AI analyses per day
              <Input type="number" min={0} max={500} defaultValue={briefing.ai_daily_limit} onBlur={(e) => setBriefingCfg({ ai_daily_limit: Number(e.target.value) })} className="w-24" data-testid="na-briefing-limit" /></label>
            <p className="text-[12px] text-slate-500 mt-2">Applies immediately. AI never contacts customers or changes records.</p>
          </>)}
        </Card>
      </div>

      <RecipientsCard value={s.recipients || {}} onChange={(r) => setS((x) => ({ ...x, recipients: r }))} />
      <StaffMatchCard Card={Card} />

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Channels" testid="na-channels">
          {[["In-app", true, false], ["Mobile push (TedOps Field)", true, false], ["Email", s.email_enabled, true], ["SMS (critical only)", s.sms_enabled, true]].map(([l, on, locked]) => (
            <div key={l} className="flex items-center justify-between py-2 text-sm"><span>{l}</span>
              <span className="flex items-center gap-2">{locked && <Lock className="w-3.5 h-3.5 text-slate-400" />}<Switch checked={!!on} disabled data-testid={`na-channel-${l.split(" ")[0].toLowerCase()}`} /></span></div>
          ))}
          <p className="text-[12px] text-slate-500 mt-2">Email and SMS stay off until switching them on is approved. Personal push choices are set by each person in TedOps Field.</p>
        </Card>
        <Card title="Quiet hours" testid="na-quiet">
          <div className="flex items-center justify-between py-1 text-sm"><span>Hold back non-critical phone pushes</span><Switch checked={!!s.quiet_hours.enabled} onCheckedChange={(v) => setQ("enabled", v)} data-testid="na-quiet-enabled" /></div>
          <div className="flex gap-3 mt-3">
            <label className="text-xs text-slate-500">From<Input type="time" value={s.quiet_hours.start} onChange={(e) => setQ("start", e.target.value)} data-testid="na-quiet-start" /></label>
            <label className="text-xs text-slate-500">To<Input type="time" value={s.quiet_hours.end} onChange={(e) => setQ("end", e.target.value)} data-testid="na-quiet-end" /></label>
          </div>
          <p className="text-[12px] text-slate-500 mt-2">Critical alerts always come through, and every notification is still saved in the Notification Centre.</p>
        </Card>
      </div>

      <Card title="Reminders, escalation and flood limits" testid="na-rules">
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
          {RULES.map(([k, l]) => (
            <label key={k} className="flex items-center justify-between gap-3 text-sm py-1 min-w-0">{l}
              <Input type="number" min="0" value={s.rules[k]} onChange={(e) => setRule(k, e.target.value)} className="w-24 h-9" data-testid={`na-rule-${k}`} /></label>
          ))}
          <label className="flex items-center justify-between gap-3 text-sm py-1">Escalate to role
            <Select value={s.rules.escalate_to_role} onValueChange={(v) => setRule("escalate_to_role", v)}>
              <SelectTrigger className="w-44 h-9" data-testid="na-rule-escalate_to_role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.filter((r) => r.key !== "super_admin").map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent>
            </Select></label>
          <label className="flex items-center justify-between gap-3 text-sm py-1">In-app daily digest<Switch checked={!!s.rules.digest_enabled} onCheckedChange={(v) => setRule("digest_enabled", v)} data-testid="na-rule-digest_enabled" /></label>
          <label className="flex items-center justify-between gap-3 text-sm py-1">Include items raised before reminders started (backlog)<Switch checked={!!s.rules.remind_backlog} onCheckedChange={(v) => setRule("remind_backlog", v)} data-testid="na-rule-remind_backlog" /></label>
        </div>
        <p className="text-[12px] text-slate-500 mt-3">Use Dry run before switching on the backlog to see exactly who would be notified.</p>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Volume (last 24 hours)" testid="na-volume">
          {Object.keys(ov?.paused || {}).length > 0 && Object.entries(ov.paused).map(([k, v]) => (
            <div key={k} className="mb-2 flex items-center justify-between rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid={`na-paused-${k}`}>
              <span>Paused: {k} ({v.count_last_hour} in an hour)</span>
              <Button size="sm" variant="outline" onClick={() => resume(k)} className="gap-1" data-testid={`na-resume-${k}`}><PlayCircle className="w-4 h-4" /> Resume</Button></div>
          ))}
          {ov?.critical && (
            <div className="mb-2 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="na-critical">
              Critical alerts this hour: <b>{ov.critical.last_hour}</b> of {ov.critical.limit_per_hour}
              {ov.critical.held_back_events_24h > 0 && <span className="text-rose-700"> · ceiling reached {ov.critical.held_back_events_24h}× in 24h</span>}
            </div>
          )}
          <div className="divide-y divide-slate-100 text-sm max-h-64 overflow-y-auto">
            {(ov?.volume_24h || []).map((v) => <div key={v.type} className="flex justify-between gap-2 py-1.5 min-w-0"><span className="truncate">{v.type}</span><span className="tabular-nums font-medium shrink-0">{v.count}</span></div>)}
            {ov && !ov.volume_24h.length && <div className="py-3 text-slate-400">No notifications in the last 24 hours.</div>}
          </div>
        </Card>
        <Card title="Delivery failures" testid="na-failures">
          <div className="divide-y divide-slate-100 text-sm max-h-64 overflow-y-auto">
            {(ov?.delivery_failures || []).map((f, i) => <div key={i} className="py-1.5 break-words"><span className="font-medium">{f.channel}</span> · {f.event_type} · <span className="text-rose-600 break-all">{f.last_error || f.status}</span></div>)}
            {ov && !ov.delivery_failures.length && <div className="py-3 text-slate-400">No failed deliveries.</div>}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Notification types" testid="na-types">
          <div className="divide-y divide-slate-100 text-sm max-h-80 overflow-y-auto">
            {(ov?.categories || []).filter((c) => c.active).map((c) => <div key={c.key} className="flex items-center justify-between gap-2 py-1.5"><span className="truncate">{c.label}</span><span className="text-xs text-slate-500 shrink-0">{c.priority} · {c.kind}{c.permission ? ` · ${c.permission}` : ""}</span></div>)}
          </div>
        </Card>
        <Card title="Policy change history" testid="na-audit">
          <div className="divide-y divide-slate-100 text-sm max-h-80 overflow-y-auto">
            {(ov?.audit || []).map((a, i) => <div key={i} className="py-1.5"><div className="truncate">{a.detail || a.action}</div><div className="text-[11px] text-slate-400">{(a.ts || "").slice(0, 16).replace("T", " ")} · {a.actor_email || a.user}</div></div>)}
            {ov && !ov.audit.length && <div className="py-3 text-slate-400">No changes yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
